import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPut } from './client';
import type {
  AdAllocation,
  AdAllocationListResponse,
  AdMatchResponse,
  AdminContentSubmissionDetail,
  AdminContentSubmissionRow,
  AdsSheetResponse,
  AnalyticsNetworkSummary,
  AnalyticsResponse,
  Call,
  CallFilters,
  CallSortField,
  CallStats,
  CallsListResponse,
  ContentPendingCountResponse,
  ContentSubmissionStatus,
  FbcPerformance,
  Franchisee,
  FunnelResponse,
  GbpHealth,
  GbpNetworkSummary,
  GbpSheetResponse,
  RankingsGrid,
  RankingsNetworkSummary,
  ListingsResponse,
  LocationAddressResponse,
  MyContentSubmission,
  RankingsResponse,
  ReviewsResponse,
  GbpReviewsSummary,
  MetaResponse,
  OrganicUnderperformer,
  ScorecardRow,
  SocialResponse,
  TimeseriesPoint,
  TopBottomResponse,
  UserRow,
  WebformLeadsResponse,
  WebsiteTrafficTopPerformer,
  WhEffectivenessResponse,
  WhHygieneResponse,
} from '../types';

export function useFranchiseesQuery() {
  return useQuery<Franchisee[]>({
    queryKey: ['franchisees'],
    queryFn: () => apiGet<Franchisee[]>('/api/franchisees'),
    staleTime: 5 * 60 * 1000,
  });
}

export function useAdMatchQuery(af: string | null) {
  return useQuery<AdMatchResponse>({
    queryKey: ['ad-match', af],
    queryFn: () => apiGet<AdMatchResponse>(`/api/ad-match?af=${encodeURIComponent(af ?? '')}`),
    enabled: !!af,
    staleTime: 60 * 60 * 1000,
  });
}

const baseAf = (af: string) => af.replace(/\.\d+$/, '');

export function useAdAllocationsQuery(af: string | null) {
  return useQuery<AdAllocationListResponse>({
    queryKey: ['ad-allocation', af ? baseAf(af) : null],
    queryFn: () =>
      apiGet<AdAllocationListResponse>(`/api/ad-allocation?af=${encodeURIComponent(af ?? '')}`),
    enabled: !!af,
    staleTime: 5 * 60 * 1000,
  });
}

export function useSaveAllocationMutation() {
  const qc = useQueryClient();
  return useMutation<AdAllocation, Error, { afSub: string; metaPct: number }>({
    mutationFn: ({ afSub, metaPct }) =>
      apiPut<AdAllocation>(`/api/ad-allocation/${encodeURIComponent(afSub)}`, { metaPct }),
    onSuccess: (saved) => {
      qc.invalidateQueries({ queryKey: ['ad-allocation', saved.afBase] });
      qc.invalidateQueries({ queryKey: ['ad-allocation-report'] });
    },
  });
}

export function useAllocationReportQuery(enabled: boolean) {
  return useQuery<AdAllocationListResponse>({
    queryKey: ['ad-allocation-report'],
    queryFn: () => apiGet<AdAllocationListResponse>('/api/ad-allocation/report'),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useWelcomeHomeEffectivenessQuery(af: string | null) {
  return useQuery<WhEffectivenessResponse>({
    queryKey: ['welcomehome-effectiveness', af],
    queryFn: () =>
      apiGet<WhEffectivenessResponse>(`/api/welcomehome/effectiveness?af=${encodeURIComponent(af ?? '')}`),
    enabled: !!af,
    staleTime: 30 * 60 * 1000,
  });
}

export function useWelcomeHomeHygieneQuery(af: string | null) {
  return useQuery<WhHygieneResponse>({
    queryKey: ['welcomehome-hygiene', af],
    queryFn: () =>
      apiGet<WhHygieneResponse>(`/api/welcomehome/hygiene?af=${encodeURIComponent(af ?? '')}`),
    enabled: !!af,
    staleTime: 30 * 60 * 1000,
  });
}

export function useSocialQuery(af: string | null) {
  return useQuery<SocialResponse>({
    queryKey: ['social', af],
    queryFn: () => apiGet<SocialResponse>(`/api/social-urls?af=${encodeURIComponent(af ?? '')}`),
    enabled: !!af,
    staleTime: 15 * 60 * 1000,
  });
}

export function useListingsQuery(af: string | null) {
  return useQuery<ListingsResponse>({
    queryKey: ['listings', af],
    queryFn: () => apiGet<ListingsResponse>(`/api/listings?af=${encodeURIComponent(af ?? '')}`),
    enabled: !!af,
    staleTime: 15 * 60 * 1000,
  });
}

export function useReviewsQuery(af: string | null) {
  return useQuery<ReviewsResponse>({
    queryKey: ['reviews', af],
    queryFn: () => apiGet<ReviewsResponse>(`/api/reviews?af=${encodeURIComponent(af ?? '')}`),
    enabled: !!af,
    staleTime: 6 * 60 * 60 * 1000,
  });
}

export function useAnalyticsQuery(af: string | null) {
  return useQuery<AnalyticsResponse>({
    queryKey: ['analytics', af],
    queryFn: () => apiGet<AnalyticsResponse>(`/api/analytics?af=${encodeURIComponent(af ?? '')}`),
    enabled: !!af,
    staleTime: 10 * 60 * 1000,
  });
}

export function useRankingsQuery(af: string | null) {
  return useQuery<RankingsResponse>({
    queryKey: ['rankings', af],
    queryFn: () => apiGet<RankingsResponse>(`/api/rankings?af=${encodeURIComponent(af ?? '')}`),
    enabled: !!af,
    staleTime: 60 * 60 * 1000,
  });
}

export function useGbpSheetQuery(af: string | null) {
  return useQuery<GbpSheetResponse>({
    queryKey: ['gbp-sheet', af],
    queryFn: () => apiGet<GbpSheetResponse>(`/api/gbp/sheet-data?af=${encodeURIComponent(af ?? '')}`),
    enabled: !!af,
    staleTime: 60 * 60 * 1000,
  });
}

export function useAdsSheetQuery(af: string | null) {
  return useQuery<AdsSheetResponse>({
    queryKey: ['ads-sheet', af],
    queryFn: () => apiGet<AdsSheetResponse>(`/api/ads/sheet-data?af=${encodeURIComponent(af ?? '')}`),
    enabled: !!af,
    staleTime: 60 * 60 * 1000,
  });
}

export function useMetaQuery(af: string | null) {
  return useQuery<MetaResponse>({
    queryKey: ['meta', af],
    queryFn: () => apiGet<MetaResponse>(`/api/meta?af=${encodeURIComponent(af ?? '')}`),
    enabled: !!af,
    staleTime: 60 * 60 * 1000,
  });
}

// ── Call Tracking ────────────────────────────────────────

export type CallsListArgs = {
  af: string | null;
  page: number;
  perPage: number;
  sortBy: CallSortField;
  sortOrder: 'asc' | 'desc';
  filters: CallFilters;
};

function buildCallsQuery(args: CallsListArgs): string {
  const sp = new URLSearchParams();
  if (args.af) sp.set('afNumber', args.af);
  sp.set('page', String(args.page));
  sp.set('perPage', String(args.perPage));
  sp.set('sortBy', args.sortBy);
  sp.set('sortOrder', args.sortOrder);
  sp.set('light', 'true');
  if (args.filters.dateFrom) sp.set('dateFrom', args.filters.dateFrom);
  if (args.filters.dateTo) sp.set('dateTo', args.filters.dateTo);
  if (args.filters.status) sp.set('status', args.filters.status);
  if (args.filters.sentiment) sp.set('sentiment', args.filters.sentiment);
  if (args.filters.search) sp.set('search', args.filters.search);
  return sp.toString();
}

export function useCallsQuery(args: CallsListArgs) {
  return useQuery<CallsListResponse>({
    queryKey: [
      'calls',
      args.af, args.page, args.perPage, args.sortBy, args.sortOrder,
      args.filters.search, args.filters.status, args.filters.sentiment,
      args.filters.dateFrom, args.filters.dateTo,
    ],
    queryFn: () => apiGet<CallsListResponse>(`/api/pulse/calls?${buildCallsQuery(args)}`),
    enabled: !!args.af,
    staleTime: 60 * 1000,
  });
}

/**
 * Fetch every call matching the current filters/sort for CSV export, paging
 * through the API (server caps perPage at 250). Bounded to 40 pages (~10k calls)
 * as a safety net.
 */
export async function fetchAllCalls(
  args: Omit<CallsListArgs, 'page' | 'perPage'>,
): Promise<Call[]> {
  const PER_PAGE = 250;
  const MAX_PAGES = 40;
  const all: Call[] = [];
  let page = 1;
  let totalPages = 1;
  do {
    const qs = buildCallsQuery({ ...args, page, perPage: PER_PAGE });
    const res = await apiGet<CallsListResponse>(`/api/pulse/calls?${qs}`);
    all.push(...(res.data ?? []));
    totalPages = res.totalPages ?? 1;
    page += 1;
  } while (page <= totalPages && page <= MAX_PAGES);
  return all;
}

export function useCallStatsQuery(af: string | null, dateFrom: string, dateTo: string) {
  return useQuery<CallStats>({
    queryKey: ['call-stats', af, dateFrom, dateTo],
    queryFn: () => {
      const sp = new URLSearchParams();
      if (af) sp.set('afNumber', af);
      if (dateFrom) sp.set('dateFrom', dateFrom);
      if (dateTo) sp.set('dateTo', dateTo);
      return apiGet<CallStats>(`/api/pulse/stats?${sp.toString()}`);
    },
    enabled: !!af,
    staleTime: 60 * 1000,
  });
}

export function useCallFunnelQuery(af: string | null, dateFrom: string, dateTo: string) {
  return useQuery<FunnelResponse>({
    queryKey: ['call-funnel', af, dateFrom, dateTo],
    queryFn: () => {
      const sp = new URLSearchParams();
      if (af) sp.set('afNumber', af);
      if (dateFrom) sp.set('dateFrom', dateFrom);
      if (dateTo) sp.set('dateTo', dateTo);
      return apiGet<FunnelResponse>(`/api/pulse/funnel?${sp.toString()}`);
    },
    enabled: !!af,
    staleTime: 60 * 1000,
  });
}

export function useCallTimeseriesQuery(
  af: string | null,
  dateFrom: string,
  dateTo: string,
  interval: 'day' | 'week' | 'month' = 'week',
) {
  return useQuery<TimeseriesPoint[]>({
    queryKey: ['call-timeseries', af, dateFrom, dateTo, interval],
    queryFn: () => {
      const sp = new URLSearchParams();
      if (af) sp.set('afNumber', af);
      if (dateFrom) sp.set('dateFrom', dateFrom);
      if (dateTo) sp.set('dateTo', dateTo);
      sp.set('interval', interval);
      return apiGet<TimeseriesPoint[]>(`/api/pulse/timeseries?${sp.toString()}`);
    },
    enabled: !!af,
    staleTime: 60 * 1000,
  });
}

// ── Network / FBC dashboard data ───────────────────────

function dateRangeQuery(dateFrom: string, dateTo: string): string {
  const sp = new URLSearchParams();
  if (dateFrom) sp.set('dateFrom', dateFrom);
  if (dateTo) sp.set('dateTo', dateTo);
  return sp.toString();
}

export function useAdminStatsQuery(dateFrom: string, dateTo: string, enabled: boolean) {
  return useQuery<CallStats>({
    queryKey: ['admin-stats', dateFrom, dateTo],
    queryFn: () => apiGet<CallStats>(`/api/pulse/stats?${dateRangeQuery(dateFrom, dateTo)}`),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useScorecardQuery(dateFrom: string, dateTo: string, enabled: boolean) {
  return useQuery<ScorecardRow[]>({
    queryKey: ['scorecard', dateFrom, dateTo],
    queryFn: () =>
      apiGet<ScorecardRow[]>(`/api/pulse/franchisee-scorecard?${dateRangeQuery(dateFrom, dateTo)}`),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useFbcPerformanceQuery(dateFrom: string, dateTo: string, enabled: boolean) {
  return useQuery<FbcPerformance[]>({
    queryKey: ['fbc-performance', dateFrom, dateTo],
    queryFn: () =>
      apiGet<FbcPerformance[]>(`/api/pulse/fbc-performance?${dateRangeQuery(dateFrom, dateTo)}`),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useRankingsGridQuery(af: string | null) {
  return useQuery<RankingsGrid>({
    queryKey: ['rankings-grid', af],
    queryFn: () => apiGet<RankingsGrid>(`/api/rankings/grid?af=${encodeURIComponent(af ?? '')}`),
    enabled: !!af,
    staleTime: 60 * 60 * 1000,
  });
}

export function useGbpHealthQuery(af: string | null) {
  return useQuery<GbpHealth>({
    queryKey: ['gbp-health', af],
    queryFn: () => apiGet<GbpHealth>(`/api/gbp/health?af=${encodeURIComponent(af ?? '')}`),
    enabled: !!af,
    staleTime: 60 * 60 * 1000,
  });
}

export function useRankingsNetworkSummaryQuery(enabled: boolean) {
  return useQuery<RankingsNetworkSummary>({
    queryKey: ['rankings-network-summary'],
    queryFn: () => apiGet<RankingsNetworkSummary>('/api/rankings/network-summary'),
    enabled,
    staleTime: 60 * 60 * 1000,
  });
}

export function useGbpNetworkSummaryQuery(enabled: boolean) {
  return useQuery<GbpNetworkSummary>({
    queryKey: ['gbp-network-summary'],
    queryFn: () => apiGet<GbpNetworkSummary>('/api/gbp/network-summary'),
    enabled,
    staleTime: 60 * 60 * 1000,
  });
}

export function useAnalyticsNetworkSummaryQuery(enabled: boolean) {
  return useQuery<AnalyticsNetworkSummary>({
    queryKey: ['analytics-network-summary'],
    queryFn: () => apiGet<AnalyticsNetworkSummary>('/api/analytics/network-summary'),
    enabled,
    staleTime: 60 * 60 * 1000,
  });
}

export function useGbpReviewsSummaryQuery(enabled: boolean) {
  return useQuery<GbpReviewsSummary>({
    queryKey: ['gbp-reviews-summary'],
    queryFn: () => apiGet<GbpReviewsSummary>('/api/admin/gbp-reviews-summary'),
    enabled,
    staleTime: 30 * 60 * 1000,
  });
}

export function useWebsiteTrafficTopPerformersQuery(enabled: boolean) {
  return useQuery<WebsiteTrafficTopPerformer[]>({
    queryKey: ['website-traffic-top-performers'],
    queryFn: () => apiGet<WebsiteTrafficTopPerformer[]>('/api/admin/website-traffic-top-performers'),
    enabled,
    staleTime: 30 * 60 * 1000,
  });
}

export function useOrganicUnderperformersQuery(enabled: boolean) {
  return useQuery<OrganicUnderperformer[]>({
    queryKey: ['organic-underperformers'],
    queryFn: () => apiGet<OrganicUnderperformer[]>('/api/admin/organic-underperformers'),
    enabled,
    staleTime: 30 * 60 * 1000,
  });
}

export function useUsersQuery(enabled: boolean) {
  return useQuery<UserRow[]>({
    queryKey: ['admin-users'],
    queryFn: () => apiGet<UserRow[]>('/api/admin/users'),
    enabled,
    staleTime: 60 * 1000,
  });
}

export function useTopBottomQuery(dateFrom: string, dateTo: string, enabled: boolean) {
  return useQuery<TopBottomResponse>({
    queryKey: ['top-bottom', dateFrom, dateTo],
    queryFn: () =>
      apiGet<TopBottomResponse>(`/api/pulse/top-bottom?${dateRangeQuery(dateFrom, dateTo)}`),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCallDetailQuery(callId: number | null) {
  return useQuery<Call>({
    queryKey: ['call-detail', callId],
    queryFn: () => apiGet<Call>(`/api/pulse/calls/${callId}`),
    enabled: callId !== null && callId !== undefined,
    staleTime: 5 * 60 * 1000,
  });
}

export function useLocationAddressQuery(af: string | null) {
  return useQuery<LocationAddressResponse>({
    queryKey: ['location-address', af],
    queryFn: () =>
      apiGet<LocationAddressResponse>(`/api/location-address?af=${encodeURIComponent(af ?? '')}`),
    enabled: !!af,
    staleTime: 60 * 60 * 1000,
  });
}

// ── Content delivery ────────────────────────────────────────────────

export function useMyContentSubmissionsQuery(af: string | null) {
  return useQuery<{ submissions: MyContentSubmission[] }>({
    queryKey: ['content-my-submissions', af],
    queryFn: () =>
      apiGet<{ submissions: MyContentSubmission[] }>(
        `/api/content/my-submissions?af=${encodeURIComponent(af ?? '')}`,
      ),
    enabled: !!af,
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000, // poll every 30s so status updates show up
  });
}

// Admin-only.
export function useContentSubmissionsQuery(status: ContentSubmissionStatus | 'all', enabled = true) {
  return useQuery<{ submissions: AdminContentSubmissionRow[] }>({
    queryKey: ['content-submissions', status],
    queryFn: () =>
      apiGet<{ submissions: AdminContentSubmissionRow[] }>(
        `/api/content/submissions?status=${encodeURIComponent(status)}`,
      ),
    enabled,
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000,
  });
}

export function useContentSubmissionDetailQuery(id: number | null) {
  return useQuery<{ submission: AdminContentSubmissionDetail }>({
    queryKey: ['content-submission-detail', id],
    queryFn: () =>
      apiGet<{ submission: AdminContentSubmissionDetail }>(
        `/api/content/submissions/${id}`,
      ),
    enabled: id != null,
    staleTime: 0,
  });
}

export function useContentPendingCountQuery(enabled = true) {
  return useQuery<ContentPendingCountResponse>({
    queryKey: ['content-pending-count'],
    queryFn: () => apiGet<ContentPendingCountResponse>('/api/content/pending-count'),
    enabled,
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000,
  });
}

// ── Webform Leads (Ninja Forms) ─────────────────────────────────────
export function useWebformLeadsQuery(af: string | null) {
  return useQuery<WebformLeadsResponse>({
    queryKey: ['webform-leads', af],
    queryFn: () =>
      apiGet<WebformLeadsResponse>(`/api/webform-leads?af=${encodeURIComponent(af ?? '')}`),
    enabled: !!af,
    staleTime: 5 * 60 * 1000,
  });
}
